"use client";

import { track } from "@/lib/analytics";
import type { CatalogPiece } from "@/lib/api/api-client";
import { ExternalLinkIcon, SearchIcon, ScoreIcon } from "./library-icons";
import { MUSESCORE_STUDIO_URL, museScoreSearchUrl } from "@/lib/library/piece-format";
import { CatalogCard } from "./catalog-card";

/**
 * L'écran vide de la recherche du catalogue.
 *
 * # Le problème qu'il traite
 *
 * Le catalogue est en domaine public, la demande ne l'est pas. Quelqu'un qui
 * tape « Interstellar » ou « Ludovico Einaudi » ne s'est pas trompé de champ :
 * il a formulé exactement ce qu'il veut jouer, et notre réponse honnête est
 * « pas ici ». Un « Aucun morceau trouvé » gris fait de ce moment un cul-de-sac
 * alors que c'est le seul de tout le produit où l'on sait *précisément* ce que
 * la personne cherche.
 *
 * # Ce qu'il propose
 *
 * Deux sorties, et elles ne répondent pas à la même situation :
 *
 *  - **Chercher sur MuseScore.com** — pour qui n'a pas la partition. Le lien
 *    part avec la requête déjà écrite et le filtre piano posé, donc la personne
 *    arrive sur des résultats, pas sur une page d'accueil.
 *  - **MuseScore Studio** — pour qui l'a déjà, sous une forme que nous savons
 *    lire. C'est la porte qui tient réellement la promesse « Virtuosa ne se
 *    limite pas au domaine public » : le fichier reste privé, nous ne le
 *    publions pas.
 *
 * Les deux sont derrière le flag `musescore_integration`. Éteint, il reste
 * l'écran vide de base — c'est délibéré : la valeur de cet écran ne dépend pas
 * de MuseScore, et une panne de flags ne doit pas produire un écran muet.
 *
 * # Ce qui est nouveau : la sélection de rebond
 *
 * Les deux sorties MuseScore mènent **hors** de Virtuosa. C'était acceptable
 * dans une modale de 900 px, où refermer la modale ramenait au produit ; ça ne
 * l'est plus sur un écran entier, où elles sont alors les deux seules choses à
 * cliquer. Un néophyte qui tape « Interstellar » le premier jour se retrouvait
 * expédié chez un tiers sans avoir jamais entendu une note de Virtuosa.
 *
 * Trois morceaux, sous « Ou essaie », rétablissent une issue interne. Ils
 * viennent de l'appelant (les premiers d'une sélection déjà chargée) : cet écran ne
 * lance aucune requête, il n'a rien à faire attendre à quelqu'un qui vient
 * d'échouer.
 *
 * # Ce qu'il ne propose pas
 *
 * Pas de bouton « Importer ». L'import vit dans le Studio et n'est pas
 * pilotable d'ici — même raison que dans « Mes morceaux » : une porte qui ne
 * s'ouvre pas coûte plus cher que pas de porte. La phrase le dit, le bouton ne
 * le ferait pas.
 */

type Props = {
  /** La requête déjà nettoyée. Vide quand seuls les critères ont vidé la liste. */
  query: string;
  /** Un sélection ou un genre est actif : le vide peut venir de là. */
  hasFilters: boolean;
  /** État de `musescore_integration`. */
  museScoreEnabled: boolean;
  /** Le rebond interne. Vide = la section n'est pas rendue. */
  suggestions: readonly CatalogPiece[];
};

export function LibraryEmpty({ query, hasFilters, museScoreEnabled, suggestions }: Props) {
  return (
    <div className="py-8 flex justify-center">
      {/*
        560 px pour le message et non 400 : à pleine largeur, une colonne de
        400 px au milieu d'un canevas de 1200 se lit comme un message d'erreur
        système. Les deux sorties passent en deux colonnes à partir de `sm`, ce
        qui les met sur la même ligne d'œil plutôt qu'en pile verticale.

        Le rebond, lui, déborde jusqu'à 720 px : à 560, trois cartes tombaient à
        176 px et leur ligne « Une main pose · Une soirée » se coupait au milieu
        — vu en rendu réel. Une carte dont la seule information utile est
        tronquée ne rattrape personne.
      */}
      <div className="w-full max-w-[720px] flex flex-col items-center text-center gap-4">
        <div className="flex flex-col gap-2 max-w-[560px]">
          <h3 className="text-[17px] font-semibold text-text-primary">
            {query !== "" ? (
              <>
                Rien pour «&nbsp;<span className="text-accent-bright">{query}</span>
                &nbsp;»
              </>
            ) : hasFilters ? (
              "Rien avec ces critères"
            ) : (
              /* Ni recherche ni critère et pourtant rien : le catalogue est
                 vraiment vide. Rare, mais accuser des filtres inexistants
                 enverrait chercher un réglage qui n'existe pas. */
              "Le catalogue est vide"
            )}
          </h3>
          {/*
            Dire *pourquoi*, et le dire sans s'excuser : le catalogue est
            restreint par choix juridique, pas par négligence. C'est aussi
            l'explication qui rend les deux sorties ci-dessous évidentes.
          */}
          <p className="text-[13px] leading-relaxed text-text-secondary">
            {hasFilters
              ? "Des critères sont actifs, et le catalogue Virtuosa ne réunit que des partitions du domaine public."
              : "Le catalogue Virtuosa ne réunit que des partitions du domaine public."}
            {museScoreEnabled ? (
              <> Ta partition, elle, n&apos;a pas à l&apos;être&nbsp;: Virtuosa lit le MusicXML.</>
            ) : null}
          </p>
        </div>

        {museScoreEnabled ? (
          <>
            <div className="w-full max-w-[560px] grid gap-2 sm:grid-cols-2">
              <ExitCard
                href={museScoreSearchUrl(query)}
                destination="musescore_com"
                hasQuery={query !== ""}
                icon={<SearchIcon />}
                title="Chercher sur MuseScore.com"
                detail={
                  query !== ""
                    ? `La recherche « ${query} », filtrée sur le piano.`
                    : "Leur catalogue de partitions piano."
                }
              />
              <ExitCard
                href={MUSESCORE_STUDIO_URL}
                destination="musescore_studio"
                hasQuery={query !== ""}
                icon={<ScoreIcon />}
                title="La créer dans MuseScore Studio"
                detail="Gratuit. Exporte en MusicXML, dépose le fichier ici."
              />
            </div>

            {/*
              Factuel, en gris : nous envoyons vers un catalogue déposé par ses
              utilisateurs, dont une bonne part d'arrangements non autorisés.
              Ne pas le dire ferait de nous les prescripteurs de ça.
            */}
            <p className="max-w-[560px] text-[11px] leading-relaxed text-text-muted">
              Les partitions de MuseScore.com sont déposées par ses membres&nbsp;: vérifie tes droits avant de les
              utiliser.
            </p>
          </>
        ) : (
          <p className="text-[12px] leading-relaxed text-text-muted">
            Les morceaux que tu importes depuis le Studio (MIDI, MusicXML ou photo d&apos;une partition) arrivent dans
            «&nbsp;Mes morceaux&nbsp;».
          </p>
        )}

        {suggestions.length > 0 && (
          <div className="w-full flex flex-col gap-3 pt-4 mt-2 border-t border-border-subtle">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-text-muted text-left">
              Ou essaie
            </span>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 text-left">
              {suggestions.slice(0, 3).map((piece) => (
                <CatalogCard key={piece.id} piece={piece} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ExitCard({
  href,
  destination,
  hasQuery,
  icon,
  title,
  detail,
}: {
  href: string;
  destination: "musescore_com" | "musescore_studio";
  hasQuery: boolean;
  icon: React.ReactNode;
  title: string;
  detail: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() =>
        track({
          name: "catalog_empty_exit_clicked",
          properties: { destination, hasQuery },
        })
      }
      className="group flex items-center gap-3 rounded-xl px-3.5 py-3 text-left transition-colors duration-150
        hover:bg-white/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      style={{
        backgroundColor: "var(--color-surface-card)",
        border: "1px solid var(--color-glass-border)",
      }}
    >
      <span className="shrink-0 text-text-secondary">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-[13px] font-medium text-text-primary">{title}</span>
        <span className="block text-[11px] leading-snug text-text-muted mt-[1px]">{detail}</span>
      </span>
      <span className="shrink-0 text-text-muted group-hover:text-text-secondary transition-colors duration-150">
        <ExternalLinkIcon />
      </span>
      <span className="sr-only">(ouvre un nouvel onglet)</span>
    </a>
  );
}
